import { resolveAbilityCooldownMs, resolveAbilityEssenceCost } from '@sorrem/shared';
import type { CharacterEquipmentLoadout, EssenceAbilityDefinition, EssenceAbilityTargeting, Skill } from '@sorrem/shared';

/** One labeled line of an ability's summary, e.g. "Cooldown - 8s". */
export interface AbilityFact {
    readonly label: string;
    readonly value: string;
}

const TARGET_LABELS: Readonly<Record<EssenceAbilityTargeting, string>> = {
    'self': 'Yourself',
    'enemy': 'An enemy',
    'ally': 'An ally',
    'doorway': 'A doorway',
    'aimed': 'Where you aim',
};

function formatSeconds(ms: number): string {
    return `${ms / 1000}s`;
}

/** An ability's essence cost as the player reads it: "Free", or "N essence". */
export function formatEssenceCost(cost: number): string {
    return cost === 0 ? 'Free' : `${cost} essence`;
}

/**
 * What an Essence ability costs, how often it can be cast, and what and how
 * it targets. Cost and cooldown are this character's, resolved from their
 * `skills` and wielded gear the way the host resolves them.
 */
export function describeAbilityFacts(
    ability: EssenceAbilityDefinition,
    skills?: readonly Skill[],
    equipped?: CharacterEquipmentLoadout,
): AbilityFact[] {
    const cost = resolveAbilityEssenceCost(ability, skills, equipped);
    const facts: AbilityFact[] = [
        { label: 'Cost', value: formatEssenceCost(cost) },
        { label: 'Cooldown', value: formatSeconds(resolveAbilityCooldownMs(ability, skills, equipped)) },
        { label: 'Target', value: TARGET_LABELS[ability.targeting] },
    ];
    if (ability.rangeTiles !== undefined) {
        facts.push({ label: 'Range', value: ability.rangeTiles === 1 ? '1 tile' : `${ability.rangeTiles} tiles` });
    }
    const holdMs = ability.holdToCastMs ?? 0;
    facts.push({ label: 'Cast', value: holdMs > 0 ? `Hold for ${formatSeconds(holdMs)}` : 'On press' });
    return facts;
}
